import {
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@mui/material";
import React, {FC, useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {AddPeriod} from "../../../../../assets/icons/AddPeriod";
import {Delete} from "../../../../../assets/icons/Delete";
import {GET_DEFAULT_MEDICATIONS_REQUEST, SET_TOAST} from "../../../../../redux/actions";
import {IMedicationForTemplate, ToastType} from "../../../../../utils/interfaces";
import {Modal} from "../../../../common/Modal/Modal";
import classes from "./CreateEditMedicationTemplateTimeline.module.scss";
import {AddEditMedicationModal} from "./AddEditMedicationModal";
import {Edit} from "../../../../../assets/icons/Edit";

export type SelectedMedicationData = {
  type: 'edit' | 'add' | 'remove' | null
  medication: IMedicationForTemplate | null
}

type CreateEditMedicationTemplateTimelineProps = {
  medications: IMedicationForTemplate[];
  setMedications: React.Dispatch<React.SetStateAction<IMedicationForTemplate[]>>;
}

export const CreateEditMedicationTemplateTimeline: FC<CreateEditMedicationTemplateTimelineProps> = ({
  medications,
  setMedications,
}) => {
  const dispatch = useDispatch();
  const haveAfterSurgeryStartDate = medications?.some(medication => !!medication.startDayAfterSurgery);

  const [selectedMedicationData, setSelectedMedicationData] = useState<SelectedMedicationData>({
    type: null,
    medication: null,
  })

  useEffect(() => {
    dispatch({
      type: GET_DEFAULT_MEDICATIONS_REQUEST,
    });
  }, [dispatch]);

  const openDeleteModal = (medicationToDelete: IMedicationForTemplate) => {
    setSelectedMedicationData({
      type: 'remove',
      medication: medicationToDelete,
    });
  };

  const closeModal = () => {
    setSelectedMedicationData({
      type: null,
      medication: null,
    })
  };

  const deleteMedication = () => {
    closeModal();
    setMedications(prev => prev.filter(item => item !== selectedMedicationData.medication));
    dispatch({
      type: SET_TOAST,
      payload: {
        open: true,
        message: "Medication was successfully deleted",
        type: ToastType.SUCCESS,
      }
    })
  };

  const addMedication = () => {
    setSelectedMedicationData({
      type: 'add',
      medication: null,
    });
  };

  const editMedication = (medication: IMedicationForTemplate) => {
    setSelectedMedicationData({
      type: 'edit',
      medication,
    });
  }

  const submitAddNewMedication = (newMedication: IMedicationForTemplate) => {
    if (selectedMedicationData.type === 'edit') {
      setMedications(prev => prev.map(item => item.id === selectedMedicationData.medication?.id ? newMedication : item));
      return;
    }

    setMedications(prev => [...prev, newMedication]);
  };

  return (
    <div className={classes.treatmentTimelineContainer}>
      <h2>Medications</h2>
      <Table>
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell align="left">Name</TableCell>
            <TableCell align="left">Dosage</TableCell>
            <TableCell align="left">Times</TableCell>
            <TableCell align="left">Duration</TableCell>
            {haveAfterSurgeryStartDate ? <TableCell align="left">Start after surgery</TableCell> : null}
            <TableCell align="left">Note</TableCell>
            <TableCell align="right"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {medications.map(
            (medication, index) => {
              return (
                <TableRow className={classes.tableRow} key={index}>
                  <TableCell align="left">{medication.name}</TableCell>
                  <TableCell align="left">{`${medication.dosageValue} ${medication.dosageUnit}` || "-"}</TableCell>
                  <TableCell align="left">
                    <div style={{display: 'flex', flexDirection: 'row', gap: 8}}>
                      {medication.times.map((time, index) => {
                        const splittedTime = time.split(':');
                        const formattedTime = `${splittedTime[0]}:${splittedTime[1]}`;

                        return (
                          <Chip size="small" key={index} label={formattedTime} variant="outlined" />
                        )
                      })}
                    </div>
                  </TableCell>
                  <TableCell align="left">{medication.duration} {`day${medication.duration > 1 ? 's' : ''}`}</TableCell>
                  {haveAfterSurgeryStartDate ? (
                    <TableCell align="left">
                      {medication.startDayAfterSurgery ? `${medication.startDayAfterSurgery} ${`day${medication.startDayAfterSurgery > 1 ? 's' : ''}`}` : '-'}
                    </TableCell>
                  ) : null}
                  <TableCell align="left">{medication.note || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => editMedication(medication)}>
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => openDeleteModal(medication)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            }
          )}
        </TableBody>
      </Table>
      <div className={classes.footer}>
        <Button
          className={classes.addPeriod}
          onClick={() => addMedication()}
          startIcon={<AddPeriod />}
          variant="text"
        >
          Add medication
        </Button>
      </div>
      <AddEditMedicationModal
        onSubmit={submitAddNewMedication}
        type={selectedMedicationData.type}
        medication={selectedMedicationData.medication}
        onClose={closeModal}
      />
      <Modal
        deleteConfirmation
        open={selectedMedicationData.type === 'remove'}
        title={"Delete medication"}
        message={"Are you sure that you want to delete this medication?"}
        handleConfirm={deleteMedication}
        handleCancel={closeModal}
      />
    </div>
  );
};
